import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { getFormWorkState } from '@shared/lib/unsavedWork';
import { useReportUnsavedWork } from './useReportUnsavedWork';

/**
 * Hook for handling unsaved changes - handles both browser and React Router navigation
 * @param {boolean} hasUnsavedChanges - Whether the form has edits (plain dirty —
 *   do NOT subtract a saving flag from it; pass that as `options.isSaving`)
 * @param {Object} options - Configuration options
 * @param {boolean} [options.isSaving] - Whether a save is in flight
 * @returns {Object} - Hook utilities
 */
const useUnsavedChangesSimple = (hasUnsavedChanges, options = {}) => {
  const {
    message = 'You have unsaved changes that will be lost if you leave this page.',
    enableBrowserWarning = true,
    isSaving = false,
  } = options;

  const navigate = useNavigate();
  const location = useLocation();

  // Two answers from one state, and they differ mid-save: the save's own
  // success handler is about to navigate, so navigation must not be
  // blocked, while the request in flight is work that an app replacement
  // would drop, so the registry must be held. See `getFormWorkState`.
  const { blocksNavigation, holdsWork } = getFormWorkState({
    dirty: hasUnsavedChanges,
    saving: isSaving,
  });

  // Report upwards, so things outside this subtree can tell whether any
  // form on the page holds unsaved work. The mandatory-update screen
  // uses it to wait rather than replace the app mid-edit. Registering
  // here rather than in each form means every guarded form is covered
  // without opting in.
  useReportUnsavedWork(holdsWork);

  const [showModal, setShowModal] = useState(false);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);
  const [pendingLocation, setPendingLocation] = useState(null);
  const [isBackNavigation, setIsBackNavigation] = useState(false);

  // Handle browser beforeunload event (page refresh/close)
  useEffect(() => {
    if (!enableBrowserWarning) return;

    const handleBeforeUnload = event => {
      if (holdsWork && !confirmedNavigation) {
        event.preventDefault();
        event.returnValue = message; // Standard browser message
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [holdsWork, confirmedNavigation, enableBrowserWarning, message]);

  // Handle browser back/forward buttons
  useEffect(() => {
    if (!blocksNavigation || confirmedNavigation) return;

    const handlePopState = event => {
      if (blocksNavigation && !confirmedNavigation) {
        // Prevent navigation by pushing current state back
        window.history.pushState(null, '', window.location.pathname);

        // Mark this as back navigation and show modal
        setIsBackNavigation(true);
        setShowModal(true);
      }
    };

    // Add a dummy state to catch back button
    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [blocksNavigation, confirmedNavigation]);

  // Handle React Router navigation interception by wrapping history.pushState
  useEffect(() => {
    if (!blocksNavigation || confirmedNavigation) return;

    // Store the original pushState function
    const originalPushState = window.history.pushState;

    // Create a wrapper that intercepts pushState calls
    const interceptedPushState = function (state, title, url) {
      // Check if this is a navigation we should block (and not our own dummy state)
      if (url && url !== window.location.pathname && blocksNavigation && !confirmedNavigation) {
        // Store where we were trying to go
        setPendingLocation({ pathname: url });
        setShowModal(true);

        return; // Don't navigate
      }

      // If no unsaved changes or navigation is confirmed, proceed
      return originalPushState.call(this, state, title, url);
    };

    window.history.pushState = interceptedPushState;

    return () => {
      window.history.pushState = originalPushState;
    };
  }, [blocksNavigation, confirmedNavigation]);

  // Handle modal actions
  const handleKeepEditing = useCallback(() => {
    setShowModal(false);
    setPendingLocation(null);
    setIsBackNavigation(false);
  }, []);

  const handleDiscardChanges = useCallback(() => {
    setConfirmedNavigation(true);
    setShowModal(false);

    // If we have a pending location (React Router navigation), go there
    if (pendingLocation) {
      setTimeout(() => {
        navigate(pendingLocation.pathname);
        setPendingLocation(null);
      }, 100);
    } else if (isBackNavigation) {
      // For browser back navigation, go back twice:
      // once to undo our preventive pushState, then actually go back
      setTimeout(() => {
        window.history.go(-2);
        setIsBackNavigation(false);
      }, 100);
    } else {
      // Fallback for other cases
      setTimeout(() => {
        window.history.back();
      }, 100);
    }
  }, [navigate, pendingLocation, isBackNavigation]);

  // Reset when navigation is confirmed
  useEffect(() => {
    if (confirmedNavigation) {
      const timer = setTimeout(() => {
        setConfirmedNavigation(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [confirmedNavigation]);

  // Reset confirmed navigation when location changes
  useEffect(() => {
    setConfirmedNavigation(false);
  }, [location.pathname]);

  return {
    // Modal state and handlers
    showModal,
    handleKeepEditing,
    handleDiscardChanges,

    // Current state
    hasUnsavedChanges: blocksNavigation,
    pendingLocation,
  };
};

export default useUnsavedChangesSimple;
